import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import { getFilterValues } from '../sbSharedRefineMenuHelpers';
import { DEFAULT_LOCATION_LABELS } from '../sbSharedRefineMenuConstants';
// components
import { SbSharedRefineMenuRadioSelectSearch } from '../SbSharedRefineMenuRadioSelect/SbSharedRefineMenuRadioSelectSearch';

import get from 'lodash.get';
import styles from './SbSharedRefineMenuFilterLocation.scss';

const getUserLocationValues = viewer => {
    const locationsByZipCode = get(viewer, 'locations.edges') || [];
    return locationsByZipCode.map(location => {
        const { displayName, urlLabel, count, linkReference } = get(location, 'node') || {};
        return {
            displayName,
            urlLabel,
            count,
            linkReference,
        };
    });
};

export const SbSharedRefineMenuFilterLocationComponent = props => {
    const {
        values: facetFilterValues,
        itemSearch: { appliedFilters },
        filterName,
        viewer,
    } = props;

    const userZipCode = useSelector(state => state.relayVariables.variables?.userZipCode);
    const userLocationValues =
        userZipCode && userZipCode !== '0' ? getUserLocationValues(viewer) : [];

    const appliedFilterValues = getFilterValues(appliedFilters, filterName).map(
        locationAppliedFilterValue => {
            return Object.assign({}, locationAppliedFilterValue, {
                count: facetFilterValues.find(
                    ({ urlLabel }) => locationAppliedFilterValue.urlLabel === urlLabel
                )?.count,
            });
        }
    );

    const validLocationLabels = new Set([
        ...DEFAULT_LOCATION_LABELS,
        ...appliedFilterValues.map(filter => filter.urlLabel),
        ...userLocationValues.map(location => location.urlLabel),
    ]);

    const orderedValues = [
        ...appliedFilterValues,
        ...facetFilterValues,
        ...userLocationValues,
    ].reduce((acc, curr) => {
        const { urlLabel } = curr;
        const isLocationAlreadyExists = acc.find(item => item.urlLabel === urlLabel);
        if (isLocationAlreadyExists) {
            return acc;
        } else if (validLocationLabels.has(curr.urlLabel)) {
            acc.push(curr);
        }
        return acc;
    }, []);

    return (
        <React.Fragment>
            <div className={styles.title}>
                <FormattedMessage
                    id="sb.SbSharedRefineMenu.filter.location.title"
                    defaultMessage="Item Ships From"
                />
            </div>
            <SbSharedRefineMenuRadioSelectSearch {...props} values={orderedValues} />
        </React.Fragment>
    );
};

SbSharedRefineMenuFilterLocationComponent.propTypes = {
    values: PropTypes.array.isRequired,
    filterName: PropTypes.string.isRequired,
    itemSearch: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    uriRef: PropTypes.string,
    refetchInFlight: PropTypes.bool,
};

export const SbSharedRefineMenuFilterLocation = createFragmentContainer(
    SbSharedRefineMenuFilterLocationComponent,
    {
        viewer: graphql`
            fragment SbSharedRefineMenuFilterLocation_viewer on Viewer {
                ...SbSharedRefineMenuRadioSelectSearch_viewer
                locations: itemFacetSearch(
                    uriRef: $uriRef
                    facetName: "location"
                    facetLimit: 10
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) {
                    edges {
                        node {
                            count
                            displayName
                            urlLabel
                            linkReference
                        }
                    }
                }
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefineMenuFilterLocation_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRefineMenuRadioSelectSearch_itemSearch
                appliedFilters {
                    name
                    values {
                        count
                        displayName
                        urlLabel
                        linkable
                        linkReference
                    }
                }
            }
        `,
    }
);
